import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_COMPANY_CODE, EMPTY_COMPANY_CODE_LIST_CONFIGURATION, EMPTY_COMPANY_CODE_LIST_CRITERIA } from "../constant/companyCodeConstant";
import { CompanyCode } from "../objects/companyCode";
import { CompanyCodeListCriteria } from "../objects/companyCodeListCriteria";
import { CompanyCodeState } from "../objects/companyCodeState";
import { setCompanyCodePreference } from "../utils/setCompanyCodePreference";
import { Column } from "../objects/column";
import { ClientBranchCompany } from "../objects/company";


const initialState: CompanyCodeState = {
    company_code_list: [],
    company_code_list_configuration: EMPTY_COMPANY_CODE_LIST_CONFIGURATION,
    company_code_list_loading: false,
    company_code_list_count: 0,
    company_code_list_criteria: EMPTY_COMPANY_CODE_LIST_CRITERIA,
    company_code: EMPTY_COMPANY_CODE,
    first_preference_loaded: false,
    delete_company_code: null,
    client_branch: null
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const setDeleteCompanyCodeReducer = (state: CompanyCodeState, action: PayloadAction<CompanyCode | null>): CompanyCodeState => ({
    ...state, delete_company_code: action.payload
});
const addCompanyCodeListConfigurationPreferenceReducer = (state: CompanyCodeState, action: PayloadAction<Preferences[]>): CompanyCodeState => ({
    ...state, company_code_list_configuration: { ...state.company_code_list_configuration, preferences: [...state.company_code_list_configuration.preferences, ...action.payload] }
});

const setCompanyCodeListConfigurationPreferenceReducer = (state: CompanyCodeState, action: PayloadAction<Preferences[]>): CompanyCodeState => ({
    ...state, company_code_list_configuration: { ...state.company_code_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: CompanyCodeState, action: PayloadAction<Preferences[]>): CompanyCodeState => {
    const { preferences, columns, order_by, order } = setCompanyCodePreference(action.payload, state.company_code_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        company_code_list_configuration: { ...state.company_code_list_configuration, preferences, columns },
        company_code_list_criteria: { ...state.company_code_list_criteria, order, order_by }
    };
};

const toggleColumnReducer = (state: CompanyCodeState, action: PayloadAction<number>): CompanyCodeState => {
    const columns = cloneDeep([...state.company_code_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, company_code_list_configuration: { ...state.company_code_list_configuration, columns } };
};

const updateCompanyCodeListReducer = (state: CompanyCodeState, action: PayloadAction<CompanyCode[]>): CompanyCodeState => ({
    ...state, company_code_list: action.payload
});
const updateCompanyCodeListCriteriaReducer = (state: CompanyCodeState, action: PayloadAction<CompanyCodeListCriteria>): CompanyCodeState => ({
    ...state, company_code_list_criteria: action.payload
});
const updateCompanyCodeReducer = (state: CompanyCodeState, action: PayloadAction<CompanyCode>): CompanyCodeState => ({
    ...state, company_code: { ...action.payload, start_date: action.payload.start_date ? new Date(action.payload.start_date) : null, end_date: action.payload.end_date ? new Date(action.payload.end_date) : null }
});
const updateCompanyCodeListLoadingReducer = (state: CompanyCodeState, action: PayloadAction<boolean>): CompanyCodeState => ({
    ...state, company_code_list_loading: action.payload
});
const updateCompanyCodeListCountReducer = (state: CompanyCodeState, action: PayloadAction<number>): CompanyCodeState => ({
    ...state, company_code_list_count: action.payload
});
const setColumnsReducer = (state: CompanyCodeState, action: PayloadAction<Column[]>): CompanyCodeState => ({
    ...state, company_code_list_configuration: { ...state.company_code_list_configuration, columns: action.payload },
});
const setCompanyReducer = (state: CompanyCodeState, action: PayloadAction<ClientBranchCompany | any | null>): CompanyCodeState => ({
    ...state, client_branch: action.payload
});

export const companyCode = createSlice({
    name: 'company_code',
    initialState,
    reducers: {
        setDeleteCompanyCode: setDeleteCompanyCodeReducer,
        addCompanyCodeListConfigurationPreference: addCompanyCodeListConfigurationPreferenceReducer,
        setCompanyCodeListConfigurationPreference: setCompanyCodeListConfigurationPreferenceReducer,
        toggleColumn: toggleColumnReducer,
        updateCompanyCodeList: updateCompanyCodeListReducer,
        updateCompanyCodeListCriteria: updateCompanyCodeListCriteriaReducer,
        updateCompanyCode: updateCompanyCodeReducer,
        updateCompanyCodeListLoading: updateCompanyCodeListLoadingReducer,
        updateCompanyCodeListCount: updateCompanyCodeListCountReducer,
        setColumns: setColumnsReducer,
        setCompany: setCompanyReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    setDeleteCompanyCode,
    addCompanyCodeListConfigurationPreference,
    setCompanyCodeListConfigurationPreference,
    toggleColumn,
    updateCompanyCodeList,
    updateCompanyCodeListCriteria,
    updateCompanyCode,
    updateCompanyCodeListLoading,
    updateCompanyCodeListCount,
    setColumns,
    setCompany
} = companyCode.actions;